(() => {
  if (customElements.get('enhansa-card-price-sync')) return;

  class EnhansaCardPriceSync extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.section = this.closest('.enhansa-buybox-review');
      if (!this.section) return;
      const source = this.querySelector('script[type="application/json"]');
      if (!source) return;
      try { this.data = JSON.parse(source.textContent); } catch (_) { return; }
      if (!this.data.hasPlans && !this.section.querySelector('[data-enhansa-card-variant]')) return;
      this.shortOptions = new Map();
      this.frequencyDetails = new Map();
      this.started = true;
      this.timers = [];
      this.onChange = () => {
        this.update();
        this.schedule();
        this.timers.forEach(clearTimeout);
        this.timers = [50, 250].map(delay => setTimeout(() => this.update(), delay));
      };
      this.onMutation = () => this.schedule();
      this.onPageShow = () => this.onChange();
      ['change', 'input', 'click', 'theme:variant:change'].forEach(name => this.section.addEventListener(name, this.onChange));
      window.addEventListener('pageshow', this.onPageShow);
      this.observer = new MutationObserver(this.onMutation);
      this.observer.observe(this.section, { childList: true, subtree: true, attributes: true, attributeFilter: ['value', 'checked', 'selected', 'class'] });
      this.update();
      if (this.data.hasPlans) this.poll = setInterval(() => { if (!document.hidden) this.update(); }, 250);
    }

    disconnectedCallback() {
      if (!this.started) return;
      ['change', 'input', 'click', 'theme:variant:change'].forEach(name => this.section.removeEventListener(name, this.onChange));
      window.removeEventListener('pageshow', this.onPageShow);
      this.observer.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = null;
      clearInterval(this.poll);
      this.timers.forEach(clearTimeout);
      this.shortOptions.forEach((label, option) => {
        if (option.getAttribute('label') === label) option.removeAttribute('label');
      });
      this.frequencyDetails.forEach((detail, select) => {
        const descriptions = (select.getAttribute('aria-describedby') || '').split(/\s+/).filter(id => id && id !== detail.id);
        if (descriptions.length) select.setAttribute('aria-describedby', descriptions.join(' '));
        else select.removeAttribute('aria-describedby');
        detail.remove();
      });
      this.started = false;
    }

    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
    }

    purchaseWidget() {
      const form = document.getElementById(this.data.formId);
      const installment = this.section.querySelector('form[data-product-form-installment]');
      return installment?.querySelector('.rc-widget') || form?.querySelector('.rc-widget') || this.section.querySelector('.form__width > .recharge-subscription-widget .rc-widget');
    }

    purchaseState() {
      if (!this.data.hasPlans) return { mode: 'onetime' };
      const form = document.getElementById(this.data.formId);
      const widget = this.purchaseWidget();
      const checked = widget?.querySelector('input[type="radio"]:checked');
      if (checked?.value === 'onetime') return { mode: 'onetime' };
      const subscription = checked && ['subsave', 'subscription'].includes(checked.value);
      const dropdown = widget?.querySelector('select[name="selling_plan"], select[name="rc_selling_plan"]');
      const field = Array.from(form?.elements || []).find(input => input.name === 'selling_plan' && !input.disabled && (input.type !== 'radio' || input.checked));
      const plan = dropdown?.value || field?.value || '';
      if (subscription || plan) return { mode: 'subscription', plan: String(plan) };
      return { mode: 'pending' };
    }

    compactFrequencyLabel(text) {
      const monthly = text.match(/^(\d+)\s+(day|week|month|year)s?\s+subscription with .+ discount$/i);
      if (monthly) return `${this.data.deliveryEveryLabel} ${monthly[1]} ${monthly[2].toLowerCase()}${monthly[1] === '1' ? '' : 's'}`;
      const detailed = text.match(/^(Deliver every \d+ days?|\d+ Day Supply)\s+\([^)]+\)$/i);
      return detailed ? detailed[1] : text;
    }

    updateFrequency() {
      if (!this.data.hasPlans) return;
      const select = this.purchaseWidget()?.querySelector('select[name="selling_plan"], select[name="rc_selling_plan"]');
      const options = Array.from(select?.options || []);
      const container = select?.closest?.('.rc-selling-plans');
      let detail = container?.querySelector('[data-enhansa-delivery-detail]');
      if (options.length === 0 || select.hidden || select.style?.display === 'none') {
        if (detail) { this.setText(detail, ''); detail.hidden = true; }
        return;
      }
      options.forEach(option => {
        // Native text, values and selection remain owned by Recharge.
        if (option.hasAttribute('label') && !this.shortOptions.has(option)) return;
        const original = option.textContent.trim();
        const compact = this.compactFrequencyLabel(original);
        if (compact !== original) {
          if (option.getAttribute('label') !== compact) option.setAttribute('label', compact);
          this.shortOptions.set(option, compact);
        } else if (this.shortOptions.has(option)) {
          option.removeAttribute('label');
          this.shortOptions.delete(option);
        }
      });
      const selected = options[select.selectedIndex];
      const full = selected?.textContent.trim() || '';
      const shortened = selected && this.shortOptions.has(selected);
      if (!container) return;
      if (!detail && shortened) {
        detail = document.createElement('p');
        detail.id = `${this.data.formId}-delivery-detail`;
        detail.className = 'enhansa-delivery-detail';
        detail.setAttribute('data-enhansa-delivery-detail', '');
        detail.setAttribute('aria-live', 'polite');
        container.appendChild(detail);
      }
      if (!detail) return;
      this.frequencyDetails.set(select, detail);
      this.setText(detail, shortened ? full : '');
      detail.hidden = !shortened;
      const descriptions = (select.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      if (!descriptions.includes(detail.id)) select.setAttribute('aria-describedby', [...descriptions, detail.id].join(' '));
    }

    setText(node, text) {
      if (node && node.textContent !== text) node.textContent = text;
    }

    update() {
      if (!this.isConnected) return;
      this.updateFrequency();
      const state = this.purchaseState();
      const context = this.section.querySelector('[data-enhansa-price-context]');
      this.setText(context, state.mode === 'onetime' ? this.data.oneTimeLabel : state.mode === 'subscription' && state.plan ? this.data.subscriptionLabel : this.data.pendingLabel);
      this.section.querySelectorAll('[data-enhansa-card-variant]').forEach(card => {
        const variant = this.data.variants[card.dataset.enhansaCardVariant];
        const price = card.querySelector('.enhansa-size-card__price');
        const daily = card.querySelector('.enhansa-size-card__daily');
        if (!variant || !price) return;
        const allocations = Object.values(variant.plans);
        const uniform = allocations.length > 0 && allocations.every(plan => plan.price === allocations[0].price && plan.daily === allocations[0].daily) ? allocations[0] : null;
        const amount = state.mode === 'onetime' && !variant.requiresPlan ? variant : state.mode === 'subscription' && state.plan ? variant.plans[state.plan] || uniform : null;
        const unavailable = state.mode === 'subscription' && state.plan && !amount;
        this.setText(price, amount ? amount.price : unavailable ? allocations.length ? this.data.selectSizeLabel : this.data.unavailableLabel : '');
        price.hidden = !amount && !unavailable;
        this.setText(daily, amount?.daily ? amount.daily + this.data.perDay : '');
        if (daily) daily.hidden = !amount?.daily;
      });
    }
  }

  customElements.define('enhansa-card-price-sync', EnhansaCardPriceSync);
})();
